import React, { useMemo } from 'react';
import { BsPlusCircle } from 'react-icons/bs';
import { Box, Flex, HStack, Icon, IconButton, Text, Tooltip, VStack } from '@chakra-ui/react';
import PropTypes from 'prop-types';

import useCompanyFeatures from '../../../../../../../../../../../../../components/Authorizer/useCompanyFeatures';
import { analyticsIds } from '../../../../../../../../../../../../../constants/analyticsIds';
import { CypressIds } from '../../../../../../../../../../../../../constants/cypressIds';
import { FeatureTypes } from '../../../../../../../../../../../../../constants/featureTypes';
import { useDocUploader } from '../../../../../../../../../../../../../services/document-service';
import { useRecordQuestionRequirements } from '../../../../../../../../../../../../../services/record-service';
import QuestionRequirementIconButton from '../../../../../../QuestionRequirementIconButton';

import BaseDocumentCell from './BaseDocumentCell';

const TOOLTIP_MIN_NAME_LENGTH = 26;

const RequirementTypeCell = ({ requirement, recordId }) => {
  const { insuredId, jobId } = requirement;
  const documentCount = requirement.documents.length;
  const showNameTooltip = (requirement.insuranceDocumentTypeDisplayName?.length ?? 0) > TOOLTIP_MIN_NAME_LENGTH;
  const { companyHasFeature: aiScanEnabled } = useCompanyFeatures(FeatureTypes.AiTierOne);

  const { data: questions, isLoading: questionsLoading } = useRecordQuestionRequirements(recordId);

  const relatedQuestions = useMemo(() => {
    if (questionsLoading) {
      return [];
    }

    return questions.filter(question => question.links.find(link => link.resourceType === 'insurance_document_type' && link.resourceId === requirement.insuranceDocumentTypeId));
  }, [questions, questionsLoading]);

  const {
    inputRef,
    handleChange,
    triggerClick
  } = useDocUploader({
    insuranceDocumentTypeId: requirement.insuranceDocumentTypeId,
    insuredId,
    recordId
  });

  if (documentCount < 2) {
    return (
      <Flex alignItems="center" justifyContent="flex-start" w="250px">
        <input
          accept=".xlsx,.xls,image/*,.doc,.docx,.txt,.pdf,.csv"
          data-cy={CypressIds.uploadDocDynamicFileInput}
          ref={inputRef}
          style={{ display: 'none' }}
          type="file"
          onChange={handleChange}
        />
        <IconButton
          aria-label="upload document"
          data-analytics-id={analyticsIds.insuredProfilePage.documentsComponent.uploadDocumentButton}
          data-cy={CypressIds.uploadDocumentButton(requirement.insuranceDocumentTypeId)}
          icon={<Icon as={BsPlusCircle} boxSize={4} cursor="pointer" />}
          variant="textPrimary"
          onClick={triggerClick}
        />
        <Tooltip isDisabled={!showNameTooltip} label={requirement.insuranceDocumentTypeDisplayName}>
          <Text
            noOfLines={1}
            whiteSpace="nowrap"
          >
            {requirement.insuranceDocumentTypeDisplayName}
          </Text>
        </Tooltip>

        {!questionsLoading && relatedQuestions?.length > 0 && (
          <QuestionRequirementIconButton
            recordId={recordId}
            resourceDisplayName={requirement.insuranceDocumentTypeDisplayName}
            resourceId={requirement.insuranceDocumentTypeId}
            resourceType="insurance_document_type"
          />
        )}
      </Flex>
    );
  }

  return (
    <VStack
      cursor="pointer"
      justifyContent="flex-start"
    >
      <HStack justifyContent="flex-start" spacing={4} w="250px">
        <input
          accept=".xlsx,.xls,image/*,.doc,.docx,.txt,.pdf"
          data-cy={CypressIds.uploadDocDynamicFileInput}
          ref={inputRef}
          style={{ display: 'none' }}
          type="file"
          onChange={handleChange}
        />
        <IconButton
          aria-label="upload document"
          data-analytics-id={analyticsIds.insuredProfilePage.documentsComponent.uploadDocumentButton}
          data-cy={CypressIds.uploadDocumentButton(requirement.insuranceDocumentTypeId)}
          icon={<Icon as={BsPlusCircle} boxSize={4} cursor="pointer" />}
          variant="textPrimary"
          onClick={triggerClick}
        />
        <Tooltip isDisabled={!showNameTooltip} label={requirement.insuranceDocumentTypeDisplayName}>
          <Text noOfLines={1}>
            {`${requirement.insuranceDocumentTypeDisplayName} (${documentCount})`}
          </Text>
        </Tooltip>

        {aiScanEnabled && !questionsLoading && relatedQuestions?.length > 0 && (
          <QuestionRequirementIconButton
            recordId={recordId}
            resourceDisplayName={requirement.insuranceDocumentTypeDisplayName}
            resourceId={requirement.insuranceDocumentTypeId}
            resourceType="insurance_document_type"
          />
        )}
      </HStack>
    </VStack>
  );
};

RequirementTypeCell.propTypes = {
  requirement: PropTypes.shape({
    insuranceDocumentTypeId: PropTypes.number,
    insuranceDocumentTypeDisplayName: PropTypes.string,
    documents: PropTypes.arrayOf(PropTypes.shape({ id: PropTypes.number })),
    insuredId: PropTypes.number,
    jobId: PropTypes.number
  }),
  recordId: PropTypes.number.isRequired
};

export default BaseDocumentCell(RequirementTypeCell);
